/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { ImageType } from '@coursera-web/cds-labs';
import { Image } from '@coursera-web/cds-labs';

import { processImage } from 'js/lib/imgix';
import { isAuthenticatedUser } from 'js/lib/user';

import type { ButtonProps } from '@coursera/cds-core';
import { Button, Hidden, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { MerchandisingModule } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import PrePurchaseUpsellCourseraPlusModal from 'bundles/coursera-plus/components/prePurchaseUpsell/PrePurchaseUpsellCourseraPlusModal';
import { useCourseraPlusSubscriptionFreeTrials } from 'bundles/coursera-plus/hooks/useCourseraPlusSubscriptionFreeTrials';

import CplusLogo from 'images/unified-common/coursera-plus-logo-invert.svg';
import BannerImageMdUp from 'images/unified-common/cplus-upsell-banner-image-mdup.png';
import MediumBannerImageSmDown from 'images/unified-common/cplus-upsell-card-med-banner-smdown.png';
import SmallBannerImageXS from 'images/unified-common/cplus-upsell-small-banner-xs.png';

import _t from 'i18n!nls/unified-common';

type Props = {
  title: string;
  description?: string;
  height: 'medium' | 'small';
  eventingData?: {
    modulePlacement: 'high' | 'low';
  };
};

const styles = {
  container: (bannerHeight: 'medium' | 'small') => css`
    min-height: ${bannerHeight === 'medium' ? 352 : 224}px;
    background-color: var(--cds-color-callouts-background-primary);
    border-radius: var(--cds-spacing-200);
    display: grid;
    grid-template-columns: 2fr 1fr;
    overflow: hidden;
  `,
  containerMediumHeight: css`
    ${breakpoints.down('sm')} {
      grid-template-columns: 1fr;
      grid-template-rows: auto auto 1fr;
    }
  `,
  content: (bannerHeight: 'medium' | 'small') => css`
    padding: ${bannerHeight === 'medium' ? 'var(--cds-spacing-1000)' : 'var(--cds-spacing-400)'} var(--cds-spacing-600);
    padding-right: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: var(--cds-spacing-200);

    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-200);
    }
  `,
  contentMediumHeight: css`
    ${breakpoints.down('sm')} {
      padding-top: var(--cds-spacing-600);
      padding-bottom: 0;
    }
  `,
  contentSmallHeight: css`
    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-100) var(--cds-spacing-50) var(--cds-spacing-100) var(--cds-spacing-200);
    }
  `,
  textContent: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  buttonContainer: css`
    padding-top: var(--cds-spacing-200);
    position: relative;
  `,
  buttonMediumHeight: css`
    ${breakpoints.down('sm')} {
      margin-left: var(--cds-spacing-200);
      position: absolute;
      white-space: nowrap;
    }
  `,
  bannerImageDiv: (bgImageUrl: string) => css`
    background-image: url(${bgImageUrl});
    background-size: cover;
    background-position: left;
    background-repeat: no-repeat;

    [dir='rtl'] & {
      transform: scaleX(-1);
    }
  `,
  bannerImageMediumHeight: css`
    ${breakpoints.down('sm')} {
      background-size: contain;
      background-position: right;
    }
  `,
};

const CourseraPlusUpsellBanner = ({ title, description, height, eventingData }: Props) => {
  const isMdUp = useMediaQuery(breakpoints.up('md'));
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const { userHasCourseraPlusFreeTrial: userIsEligibleForFreeTrial } = useCourseraPlusSubscriptionFreeTrials();

  const merchandisingModuleForEventing: MerchandisingModule = {
    moduleType: height === 'medium' ? 'coursera_plus_upsell_banner_medium' : 'coursera_plus_upsell_banner_small',
    heading: title,
    body: description,
    hasImage: true,
    position: eventingData?.modulePlacement,
  };
  const visibilityTrackerRef = useVisibilityTracker('view_merchandising_module', {
    merchandisingModule: merchandisingModuleForEventing,
  });
  const track = useTracker();

  const cplusLogoEyebrow: ImageType = {
    url: CplusLogo,
    height: isMdUp ? 18 : 12,
    useHeightPixelValue: true,
  };

  const getBannerImage = () => {
    let bannerImageUrl = BannerImageMdUp;

    if (!isMdUp) {
      bannerImageUrl = height === 'small' ? SmallBannerImageXS : MediumBannerImageSmDown;
    }

    return (
      <div
        css={[
          styles.bannerImageDiv(processImage(bannerImageUrl)),
          height === 'medium' ? styles.bannerImageMediumHeight : undefined,
        ]}
      />
    );
  };

  const getUpsellButton = () => {
    const buttonProps: Partial<ButtonProps> = {
      variant: 'primaryInvert',
      size: 'small',
      fullWidth: false,
    };

    const buttonCss = height === 'medium' ? styles.buttonMediumHeight : undefined;

    if (!isAuthenticatedUser()) {
      return (
        <Button
          css={buttonCss}
          component="a"
          href="/courseraplus"
          onClick={() =>
            track('click_merchandising_module', {
              merchandisingModule: merchandisingModuleForEventing,
              button: { name: 'c_plus_learn_more', linkType: 'internal', linkURL: '/courseraplus' },
            })
          }
          {...buttonProps}
        >
          {
            // eslint-disable-next-line @coursera/custom-rules/informative-links
            _t('Learn more')
          }
        </Button>
      );
    }

    return (
      <Button
        css={buttonCss}
        onClick={() => {
          track('click_merchandising_module', {
            merchandisingModule: merchandisingModuleForEventing,
            button: { name: 'start_free_trial' },
          });
          setIsModalOpen(true);
        }}
        {...buttonProps}
      >
        {userIsEligibleForFreeTrial ? _t('Start free trial') : _t('Renew subscription')}
      </Button>
    );
  };

  return (
    <React.Fragment>
      <PrePurchaseUpsellCourseraPlusModal
        handleClose={() => setIsModalOpen(false)}
        isOpen={isModalOpen}
        hasCourseraPlusFreeTrial={userIsEligibleForFreeTrial}
      />
      <div
        css={[styles.container(height), height === 'medium' ? styles.containerMediumHeight : undefined]}
        ref={visibilityTrackerRef as React.MutableRefObject<HTMLDivElement | null>}
      >
        <div
          css={[styles.content(height), height === 'medium' ? styles.contentMediumHeight : styles.contentSmallHeight]}
        >
          <Image {...cplusLogoEyebrow} />
          <div css={styles.textContent}>
            <Typography2 variant="titleSmall" color="invertBody" component="p">
              {title}
            </Typography2>

            {description && (
              <Hidden smDown>
                <Typography2 variant="bodyPrimary" color="invertBody" component="p">
                  {description}
                </Typography2>
              </Hidden>
            )}
          </div>

          {(isMdUp || height === 'small') && getUpsellButton()}
        </div>

        {!isMdUp && height === 'medium' && <div css={styles.buttonContainer}>{getUpsellButton()}</div>}

        {getBannerImage()}
      </div>
    </React.Fragment>
  );
};

export default CourseraPlusUpsellBanner;
